import { usePortalTranslate } from '@/hooks'
import { formatCurrency } from '@/utils'
import { useTranslation } from 'react-i18next'
import { ProductTag } from '@/ui/components/process-details'
import { ReturnedItem } from '@/domain/returns'
import { Checkbox } from '@itsrever/design-system'

interface ProductPreviewProps {
  lineItem: ReturnedItem
  restocked?: boolean
}

export const ProductPreview: React.FC<ProductPreviewProps> = ({
  lineItem,
  restocked
}) => {
  const { t } = useTranslation()
  const { trans } = usePortalTranslate({})

  const product = lineItem.product

  const unitPrice = formatCurrency(
    parseFloat(lineItem.unitPrice.amount).toString(),
    lineItem.total.currency
  )

  return (
    <div className="w-full">
      <div className="flex w-full items-center justify-between gap-2">
        <div className="flex items-start gap-2">
          {product.imageUrl ? (
            <img
              className="h-16 max-w-24 rounded-lg object-cover"
              src={product.imageUrl}
            />
          ) : (
            <span className="inline-flex size-16 items-center justify-center rounded-lg bg-neutral-100 text-neutral-600">
              <svg
                width="18px"
                height="18px"
                strokeWidth="1.5"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                color="currentColor"
              >
                <path
                  d="M21 3.6V20.4C21 20.7314 20.7314 21 20.4 21H3.6C3.26863 21 3 20.7314 3 20.4V3.6C3 3.26863 3.26863 3 3.6 3H20.4C20.7314 3 21 3.26863 21 3.6Z"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M3 16L10 13L21 18"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M16 10C14.8954 10 14 9.10457 14 8C14 6.89543 14.8954 6 16 6C17.1046 6 18 6.89543 18 8C18 9.10457 17.1046 10 16 10Z"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </span>
          )}
          <div>
            <p className="mb-1 text-[#0E121BFF]">{product.productName}</p>
            <div className="flex items-center gap-2">
              {product.variantName ? (
                <ProductTag label={product.variantName} />
              ) : null}
              {product.sku ? <ProductTag label={`SKU ${product.sku}`} /> : null}
            </div>
          </div>
        </div>
        <div>
          <span className="mr-2 text-[#99A0AEFF]">{lineItem.quantity} x</span>
          <p className="ml-auto inline text-[#335CFFFF]">{unitPrice}</p>
        </div>
      </div>
      <div className="space-y-1.5">
        <div className="flex justify-between gap-2">
          <div>
            <span className="text-xs text-grey-2">
              {lineItem.action === 'EXCHANGE'
                ? t('summary.reason_exchange')
                : t('summary.reason_refund')}
            </span>
            <span className="text-xs">
              {trans(`select_reason_${lineItem.returnReasonKey}`)}
            </span>
          </div>
          {restocked ? (
            <div className="flex cursor-default items-center justify-end gap-2">
              <Checkbox
                checked
                disabled
                styled={true}
                style={{
                  backgroundColor: '#e5e5e5',
                  color: '#525252'
                }}
              />
              <p className="text-sm text-neutral-600">
                {t('products_tab.restocked')}
              </p>
            </div>
          ) : null}
        </div>
        {lineItem.returnReasonComment ? (
          <div className="flex cursor-pointer justify-start gap-1.5 rounded-lg bg-[#F5F7FAFF] p-3">
            <svg
              width="14px"
              height="14px"
              strokeWidth="2"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              color="currentColor"
              className="mt-0.5 shrink-0 text-gray-700"
            >
              <path
                d="M7 12L17 12"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              ></path>
              <path
                d="M7 8L13 8"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              ></path>
              <path
                d="M3 20.2895V5C3 3.89543 3.89543 3 5 3H19C20.1046 3 21 3.89543 21 5V15C21 16.1046 20.1046 17 19 17H7.96125C7.35368 17 6.77906 17.2762 6.39951 17.7506L4.06852 20.6643C3.71421 21.1072 3 20.8567 3 20.2895Z"
                stroke="currentColor"
                strokeWidth="2"
              ></path>
            </svg>
            <dl className="space-y-1">
              <dt className="font-roobert text-xs font-medium text-gray-700">
                {t('products_tab.additional_comment')}
              </dt>
              <dd className="font-roobert text-xs font-normal text-neutral-900">
                {lineItem.returnReasonComment}
              </dd>
            </dl>
          </div>
        ) : null}
      </div>
    </div>
  )
}
